import React, { useState } from 'react';
import { Button } from './Button';

// Проверяем, не запретил ли браузер геолокацию для сайта заранее:
// если доступ уже отклонён, вызов getCurrentPosition гарантированно
// вернёт ошибку — вместо этого сразу показываем инструкцию
const getPermissionState = async () => {
  try {
    const permission = await navigator.permissions?.query?.({ name: 'geolocation' });
    return permission?.state || null;
  } catch {
    return null;
  }
};

/**
 * Компонент для запроса геопозиции пользователя
 * Показывает модальное окно с объяснением и кнопками разрешить/позже
 */
function GeolocationRequest({ onComplete, onSkip }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDenied, setIsDenied] = useState(false);

  const requestGeolocation = async () => {
    setIsLoading(true);
    setError(null);
    setIsDenied(false);

    if (!navigator.geolocation) {
      setError('Ваш браузер не поддерживает геолокацию. Город можно указать вручную в профиле.');
      setIsLoading(false);
      return;
    }

    const permissionState = await getPermissionState();
    if (permissionState === 'denied') {
      setIsDenied(true);
      setIsLoading(false);
      return;
    }

    const geoOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0
    };

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, geoOptions);
      });

      const coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      onComplete(coordinates);
    } catch (err) {
      console.error('❌ Ошибка получения геопозиции:', err);

      let errorMessage = 'Не удалось определить местоположение';
      let denied = false;

      switch (err.code) {
        case err.PERMISSION_DENIED:
          errorMessage = 'Доступ к геолокации запрещен для этого сайта.';
          denied = true;
          break;
        case err.POSITION_UNAVAILABLE:
          errorMessage = 'Информация о местоположении недоступна. Попробуйте еще раз.';
          break;
        case err.TIMEOUT:
          errorMessage = 'Время ожидания истекло. Попробуйте еще раз.';
          break;
        default:
          errorMessage = 'Произошла неизвестная ошибка';
      }

      setError(errorMessage);
      setIsDenied(denied);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
      <div className="relative w-full bg-[#043939] rounded-[24px] p-5 flex flex-col items-center">
        {/* Крестик: закрыть окно без запроса геолокации */}
        <button
          type="button"
          onClick={onSkip}
          aria-label="Закрыть"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="w-[80px] h-[80px] bg-[#a1f69e]/20 rounded-full flex items-center justify-center mb-6 mt-3">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
              fill="#a1f69e"
            />
          </svg>
        </div>

        <h2 className="text-white text-[20px] font-bold text-center mb-3">Доступ к местоположению</h2>

        <p className="text-white opacity-80 text-[16px] font-semibold text-center mb-6 leading-relaxed">
          Мы используем его, чтобы показывать вам людей из вашего города. Никто не увидит ваше точное местоположение
        </p>

        {/* Ошибка */}
        {error && !isDenied && (
          <div className="w-full bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-[13px] text-center">{error}</p>
          </div>
        )}

        {/* Браузер запретил геолокацию: объясняем, где это включить */}
        {isDenied && (
          <div className="w-full bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-[13px] font-semibold text-center mb-2">
              {error || 'Доступ к геолокации запрещен для этого сайта.'}
            </p>
            <ul className="text-red-200/90 text-[12px] leading-relaxed list-disc pl-4 space-y-1">
              <li>Нажмите на иконку замка (или замка с перечёркиванием) слева от адресной строки</li>
              <li>Разрешите «Местоположение» и перезагрузите страницу</li>
              <li>Проверьте, что геолокация не отключена в настройках самого устройства</li>
              <li>Сайт должен открываться по https — на http браузер всегда запрещает геолокацию</li>
            </ul>
          </div>
        )}

        {/* Кнопки */}
        <div className="w-full flex flex-col gap-3">
          <Button onClick={requestGeolocation} disabled={isLoading} className="w-full">
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Определяем...
              </span>
            ) : isDenied ? (
              'Я разрешил — проверить снова'
            ) : (
              'Разрешить'
            )}
          </Button>

          <button
            type="button"
            onClick={onSkip}
            className="w-full py-2 text-white opacity-70 hover:opacity-100 text-[15px] font-semibold transition-opacity"
          >
            Позже
          </button>
        </div>
      </div>
    </div>
  );
}

export default GeolocationRequest;
